import React, { useState } from 'react';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onViewDetails }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Live': return 'text-success bg-success/10 border-success/20';
      case 'In Development': return 'text-warning bg-warning/10 border-warning/20';
      case 'Completed': return 'text-primary bg-primary/10 border-primary/20';
      default: return 'text-text-muted bg-muted border-border';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Web App': return 'Globe';
      case 'Mobile App': return 'Smartphone';
      case 'API': return 'Server';
      case 'Desktop App': return 'Monitor';
      default: return 'Code';
    }
  };

  return (
    <div 
      className={`group bg-card border border-border rounded-xl overflow-hidden transition-premium hover-lift hover:shadow-xl ${
        isHovered ? 'border-primary/30' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Project Image */}
      <div className="relative h-48 overflow-hidden bg-muted">
        <Image
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-premium group-hover:scale-105"
        />
        
        {/* Overlay on Hover */}
        <div className={`absolute inset-0 bg-black/60 flex items-center justify-center transition-premium ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="flex space-x-3">
            {project.liveUrl && (
              <Button
                variant="default"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(project.liveUrl, '_blank');
                }}
              >
                Live Demo
              </Button>
            )}
            {project.githubUrl && (
              <Button
                variant="outline"
                size="sm"
                iconName="Github"
                iconPosition="left"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(project.githubUrl, '_blank');
                }}
                className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20"
              >
                Code
              </Button>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(project.status)}`}>
            {project.status}
          </span>
        </div>

        {/* Type Badge */}
        <div className="absolute top-3 left-3">
          <div className="flex items-center space-x-1 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-full">
            <Icon name={getTypeIcon(project.type)} size={12} className="text-primary" />
            <span className="text-xs font-medium text-text-primary">{project.type}</span>
          </div>
        </div>
      </div>

      {/* Project Content */}
      <div className="p-6">
        {/* Title and Description */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-text-primary mb-2 group-hover:text-primary transition-premium">
            {project.title}
          </h3>
          <p className="text-sm text-text-secondary line-clamp-2">
            {project.description}
          </p>
        </div>

        {/* Tech Stack */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {project.techStack.slice(0, 4).map((tech, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs font-medium bg-surface border border-border rounded-md text-text-secondary"
              >
                {tech}
              </span>
            ))}
            {project.techStack.length > 4 && (
              <span className="px-2 py-1 text-xs font-medium bg-primary/10 border border-primary/20 rounded-md text-primary">
                +{project.techStack.length - 4} more
              </span>
            )}
          </div>
        </div>

        {/* Metrics */}
        {project.metrics && (
          <div className="mb-4 grid grid-cols-2 gap-3">
            {project.metrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-lg font-bold text-primary">{metric.value}</div>
                <div className="text-xs text-text-muted">{metric.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onViewDetails(project)}
            className="text-text-secondary hover:text-primary"
          >
            View Details
          </Button>

          <div className="flex items-center space-x-2">
            {project.featured && (
              <div className="flex items-center space-x-1 text-warning">
                <Icon name="Star" size={14} className="fill-current" />
                <span className="text-xs font-medium">Featured</span>
              </div>
            )}
            
            <div className="flex items-center space-x-1 text-text-muted">
              <Icon name="Calendar" size={14} />
              <span className="text-xs">{project.year}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;